import { useState } from "react";

import {
  Button,
  ButtonGroup,
  Classes,
  Colors,
  Icon,
  IconName,
  InputGroup,
  Intent,
  Position,
} from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router";

import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsToMdQuery, Media } from "@/components/layout/Media";
import { Filter } from "@/components/pieces/filter/Filter";
import { FilterButton } from "@/components/pieces/filter/FilterButton";
import { STANDARD_NAV_HEIGHT } from "@/css/constants";
import {
  CustomerUser,
  RoleView,
  StaffReviewsUser,
  StaffSessionsUser,
} from "@/types/users";
import { Workspace } from "@/types/workspaces";

import { Links } from "./Links";

interface ClientNavProps {
  client?: StaffSessionsUser | CustomerUser | StaffReviewsUser | null;
  currentWorkspaceId?: string;
  workspaces: Workspace[];
  onSelectWorkspace?: (id: string) => void;
  view?: RoleView;
  showLinks?: boolean;
}

export function ClientNav({
  client,
  currentWorkspaceId,
  workspaces,
  onSelectWorkspace,
  view = "coach",
  showLinks = true,
}: ClientNavProps) {
  const loading = !client;
  const isXsToMd = useMediaQuery(isXsToMdQuery);
  const currentWorkspace = workspaces.find((w) => w.id === currentWorkspaceId);

  const filterOptions = workspaces.map(({ id, name, active }) => ({
    id,
    name: `${name}${!active ? ` - Inactive Workspace` : ``}`,
  }));

  const selectedOptions = filterOptions.filter(
    (option) => option.id === currentWorkspaceId,
  );

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      className="bbs"
      style={{ backgroundColor: Colors.WHITE }}
      px={3}
      width={1}
      flexDirection={isXsToMd ? "column" : "row"}
    >
      <Flex
        alignItems="center"
        justifyContent={isXsToMd ? "center" : "flex-start"}
        className={isXsToMd ? "bbs" : undefined}
        height={STANDARD_NAV_HEIGHT}
        width={1}
      >
        <Box style={{ color: Colors.GRAY3 }} mr="6px">
          <Link to="/clients">Clients</Link>
        </Box>
        <Icon icon="chevron-right" color={Colors.GRAY3} />
        <Box ml="6px">
          {client?.id ? (
            <Link to={`/client/${client.id}`}>
              {client.name || client.email}
            </Link>
          ) : (
            <Box className={Classes.SKELETON} width={100} height={35} />
          )}
        </Box>

        {view === "coach" && (
          <>
            <Icon icon="chevron-right" color={Colors.GRAY3} />
            <Box ml="6px">
              {currentWorkspace && !loading ? (
                <>
                  <Flex justifyContent="space-between" alignItems="center">
                    {workspaces.length > 1 ? (
                      <Filter
                        button={
                          <FilterButton
                            rightIcon="chevron-down"
                            text={`${currentWorkspace && currentWorkspace.name}${
                              !currentWorkspace.active
                                ? ` - Inactive Workspace`
                                : ``
                            }`}
                            icon={!currentWorkspace.active ? "disable" : null}
                            intent={
                              !currentWorkspace.active
                                ? Intent.DANGER
                                : undefined
                            }
                          />
                        }
                        options={filterOptions}
                        selectedOptions={selectedOptions}
                        multiple={false}
                        onFilterChange={(option) =>
                          onSelectWorkspace?.(option.id)
                        }
                        menuPosition="bottom-end"
                      />
                    ) : (
                      <div className="font-semibold text-normal px-1">
                        {currentWorkspace.name}
                      </div>
                    )}
                    <Media greaterThan="sm">
                      <Box ml={2}>
                        <ShareClientLink
                          clientWorkspace={
                            (currentWorkspace && currentWorkspace.urlSlug) ||
                            "www"
                          }
                        />
                      </Box>
                    </Media>
                  </Flex>
                </>
              ) : (
                <Box className={Classes.SKELETON} width={100} height={35} />
              )}
            </Box>
          </>
        )}
      </Flex>
      {showLinks && <Links client={client} />}
    </Flex>
  );
}

function getShareUrl(clientWorkspace = "www") {
  let sharedURL = document.location.href;
  const hostname = document.location.hostname;
  if (IS_PROD) {
    const parts = hostname.split(".");
    parts[0] = clientWorkspace;
    const newHostName = parts.join(".");
    sharedURL = sharedURL.replaceAll(hostname, newHostName);
  } else {
    sharedURL = sharedURL.replaceAll(hostname, `${clientWorkspace}.localhost`);
  }

  sharedURL = sharedURL.replace(new RegExp("\\/client\\/\\d+"), "");

  return sharedURL;
}

function ShareClientLink({ clientWorkspace = "www" }) {
  const [icon, setIcon] = useState<IconName>("clipboard");
  const [isCopyPopoverOpen, setIsCopyPopoverOpen] = useState(false);
  const shareUrl = getShareUrl(clientWorkspace);
  const handleSave = () => {
    navigator.clipboard.writeText(shareUrl);
    setIcon("small-tick");
    setIsCopyPopoverOpen(true);
    setTimeout(() => {
      setIsCopyPopoverOpen(false);
    }, 3000);
  };

  const iconStyle =
    icon === "clipboard"
      ? {
          color: Colors.GRAY1,
          cursor: "pointer",
        }
      : {
          color: Colors.GREEN1,
        };

  return (
    <Popover
      onClose={() => {
        setIcon("clipboard");
      }}
      position={Position.BOTTOM}
      content={
        <ButtonGroup>
          <Box width={400} p={3}>
            <InputGroup
              style={{
                backgroundColor: Colors.LIGHT_GRAY2,
                color: Colors.GRAY1,
              }}
              value={shareUrl}
              autoFocus={true}
              rightElement={
                <Popover
                  isOpen={isCopyPopoverOpen}
                  position={Position.TOP}
                  content={<Box p={2}> Copied! </Box>}
                >
                  <Button
                    minimal={true}
                    style={iconStyle}
                    icon={icon}
                    onClick={handleSave}
                  />
                </Popover>
              }
            />
            <Box mt={3}> Share this link to your dedicated client </Box>
          </Box>
        </ButtonGroup>
      }
    >
      <Button rightIcon="caret-down" intent="success">
        Share Link
      </Button>
    </Popover>
  );
}
